import { useEffect, useState } from 'react';

import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

// Reserved control-character tokens accepted/shown in Text mode, keyed by ascii code.
const RESERVED_TOKENS: Array<[code: number, name: string]> = [
  [0, 'NUL'],
  [1, 'SOH'],
  [2, 'STX'],
  [3, 'ETX'],
  [4, 'EOT'],
  [5, 'ENQ'],
  [6, 'ACK'],
  [7, 'BEL'],
  [8, 'BS'],
  [9, 'TAB'],
  [10, 'LF'],
  [11, 'VT'],
  [12, 'FF'],
  [13, 'CR'],
  [27, 'ESC'],
  [32, 'SP']
];

const CHAR_TO_TOKEN: Record<string, string> = Object.fromEntries(
  RESERVED_TOKENS.map(([code, name]) => [String.fromCharCode(code), `{${name}}`])
);

const TOKEN_TO_CHAR: Record<string, string> = Object.fromEntries(RESERVED_TOKENS.map(([code, name]) => [name, String.fromCharCode(code)]));

function asciiToAsciiText(code: number | null): string {
  return code !== null && Number.isFinite(code) ? String(code) : '';
}

// Renders the ascii code as its reserved token (eg. {STX}), a literal visible character, or a \uXXXX escape.
function asciiToText(code: number | null): string {
  if (code === null || !Number.isFinite(code)) return '';

  const char = String.fromCharCode(code);
  const token = CHAR_TO_TOKEN[char];
  if (token) return token;

  if (code < 32 || code === 127) return `\\u${code.toString(16).padStart(4, '0')}`;

  return char;
}

// Accepts a reserved token, a \uXXXX escape, or a single visible character; returns its ascii code, or null while incomplete/invalid.
function parseText(text: string): number | null {
  const tokenMatch = text.match(/^\{([A-Za-z]+)\}$/);
  if (tokenMatch) {
    const char = TOKEN_TO_CHAR[tokenMatch[1].toUpperCase()];
    return char !== undefined ? char.charCodeAt(0) : null;
  }

  const hexMatch = text.match(/^\\u([0-9a-fA-F]{4})$/);
  if (hexMatch) {
    return Number.parseInt(hexMatch[1], 16);
  }

  return text.length === 1 ? text.charCodeAt(0) : null;
}

export type AsciiCharFieldMode = 'ascii' | 'text';

type AsciiCharFieldProps = {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
};

export default function AsciiCharField({
  label,
  value,
  onChange,
  disabled = false,
  helperText,
  fullWidth = true,
  size = 'medium'
}: AsciiCharFieldProps) {
  const [mode, setMode] = useState<AsciiCharFieldMode>('text');
  const [text, setText] = useState(() => asciiToText(value));

  // Re-sync the display text whenever the underlying value or mode changes - covers a value that
  // arrives after the initial render (eg. async load) as well as switching between Ascii/Text.
  useEffect(() => {
    setText(mode === 'ascii' ? asciiToAsciiText(value) : asciiToText(value));
  }, [value, mode]);

  const handleTextChange = (nextText: string) => {
    setText(nextText);

    if (nextText === '') {
      onChange(null);
      return;
    }

    if (mode === 'ascii') {
      const n = Number(nextText);
      if (Number.isFinite(n)) onChange(Math.trunc(n));
      return;
    }

    const code = parseText(nextText);
    if (code !== null) onChange(code);
  };

  const handleBlur = () => {
    // Snap back to the canonical representation, discarding any unresolved partial input (eg. "{ST").
    setText(mode === 'ascii' ? asciiToAsciiText(value) : asciiToText(value));
  };

  return (
    <TextField
      fullWidth={fullWidth}
      size={size}
      label={label}
      helperText={helperText ?? (mode === 'ascii' ? 'Ascii code, eg. 2, 10, 58' : 'Character or reserved token, eg. : or {STX}')}
      value={text}
      onChange={(e) => handleTextChange(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Select
                variant="standard"
                disableUnderline
                value={mode}
                onChange={(e) => setMode(e.target.value as AsciiCharFieldMode)}
                disabled={disabled}
                sx={{ fontSize: '0.75rem' }}
              >
                <MenuItem value="ascii">Ascii</MenuItem>
                <MenuItem value="text">Text</MenuItem>
              </Select>
            </InputAdornment>
          )
        }
      }}
    />
  );
}
